import { EllipsisVertical, Images, MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useNewTour } from "../components/NewTourModal";
import { ProjectEditModal } from "../components/ProjectEditModal";
import { TourCard } from "../components/TourCard";
import { Button, Card, DropMenu } from "../components/ui";
import { useT } from "../i18n";
import { usePageCrumbs } from "../lib/page-header";
import { useProjects } from "../lib/projects";

/**
 * Ein Objekt und seine Rundgänge. Die Daten kommen aus derselben Liste wie
 * Seitenleiste und Übersicht — ein eigener Endpunkt lohnt dafür nicht.
 */
export function ProjectPage() {
  const { id = "" } = useParams();
  const { projects, error, reload } = useProjects();
  // Ein Dialog, zwei Einstiege — welcher, entscheidet der Menüeintrag
  const [modal, setModal] = useState<"edit" | "delete" | null>(null);
  const newTour = useNewTour();
  const t = useT();

  const project = projects?.find((p) => p.id === id) ?? null;
  usePageCrumbs([{ label: t.app.navTours, to: "/" }, { label: project?.name ?? t.project.title }]);

  if (error) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-8">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </main>
    );
  }
  if (projects === null) {
    return (
      <main className="mx-auto w-full max-w-5xl px-4 py-8">
        <p className="text-sm text-zinc-400">{t.common.loading}</p>
      </main>
    );
  }
  // Gelöscht oder fremder Account: zurück zur Übersicht statt leerer Seite
  if (!project) return <Navigate to="/" replace />;

  return (
    <main className="mx-auto w-full max-w-5xl px-4 py-8">
      <div className="mb-6 flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-lg font-semibold">{project.name}</h2>
          {project.address && (
            <p className="mt-0.5 flex items-center gap-1 text-sm text-zinc-500 dark:text-zinc-400">
              <MapPin className="size-3.5 shrink-0 text-zinc-400" />
              <span className="truncate">{project.address}</span>
            </p>
          )}
        </div>
        {/* Dieselbe Anordnung wie auf der Tourseite: Handlung vorn, Verwaltung im Menü */}
        <div className="flex shrink-0 gap-2">
          <Button onClick={() => newTour(project.id)}>
            <Plus className="size-4" />
            {t.dashboard.newTour}
          </Button>
          <DropMenu
            button={<EllipsisVertical className="size-4" />}
            title={t.project.moreActions}
            items={[
              {
                label: t.project.edit,
                icon: <Pencil className="size-4" />,
                onClick: () => setModal("edit"),
              },
              "divider" as const,
              {
                label: t.project.delete,
                icon: <Trash2 className="size-4" />,
                danger: true,
                onClick: () => setModal("delete"),
              },
            ]}
          />
        </div>
      </div>

      {project.tours.length === 0 ? (
        <Card>
          <div className="py-12 text-center">
            <Images className="mx-auto mb-3 size-8 text-zinc-300 dark:text-zinc-600" />
            <p className="font-medium">{t.project.emptyTitle}</p>
            <p className="mb-5 mt-1 text-sm text-zinc-500 dark:text-zinc-400">
              {t.dashboard.emptyText}
            </p>
            <Button onClick={() => newTour(project.id)}>
              <Plus className="size-4" />
              {t.dashboard.newTour}
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {project.tours.map((tour) => (
            <TourCard key={tour.id} tour={tour} />
          ))}
        </div>
      )}

      <ProjectEditModal
        project={modal ? project : null}
        mode={modal ?? "edit"}
        onClose={() => setModal(null)}
        onSaved={reload}
      />
    </main>
  );
}
